<script setup lang="ts">
import type { Attr } from "~/types";
import { useRouteQuery } from "@vueuse/router";
import { useSetSekouQuery } from "~/composables/useSekouQuery";

// 分類
const attrs = inject<Attr[]>("attrs", []);

const props = defineProps<{
  // attr_numで絞り込んだ場合のパラメーター
  attr_num?: number | string;
  // 商品コード
  item_cd?: string;
  cate_cd?: string;
}>();

// 現在のページ番号
const current = useRouteQuery("off", 0, {
  transform: (a) => parseInt(a.toString()),
});

// 分類
const attr_num = computed(() => (props.attr_num ? props.attr_num : ""));

// 商品コード
const item_cd = ref(props.item_cd ? props.item_cd : "");
const cate_cd = ref(props.cate_cd ? props.cate_cd : "");

let perpage = 30;
if (useIsMobile()) {
  perpage = 12;
}

type query = {
  attr_num: typeof attr_num;
  item_cd: typeof item_cd;
  cate_cd: typeof cate_cd;
};

// 分類の選択場合のみの時、カウントは取得しない
function isAttrOnly(query: query) {
  return query.attr_num.value && !query.item_cd.value && !query.cate_cd.value;
}

/**
 * 件数取得処理
 */
async function fetchCount(query: query) {
  let count = 0;
  if (isAttrOnly(query)) {
    count = attrs.find((a) => a.attr_num == query.attr_num.value)?.count || 0;
  }

  if (!count) {
    const { data } = await useFetch("/api/sekou/list/count", {
      query: {
        attr_num,
        item_cd,
        cate_cd,
        no_count: isAttrOnly(query),
      },
    });

    count = data?.value?.count ?? 0;
  }

  return { data: ref({ count, max: Math.ceil(count / perpage) }) };
}

// 施工例一覧
const [{ data, pending }, { data: countData }] = await Promise.all([
  useFetch("/api/sekou/list", {
    query: {
      attr_num,
      item_cd,
      cate_cd,
      off: current,
      perpage,
      is_count: !isAttrOnly({ attr_num, item_cd, cate_cd }),
    },
  }),
  fetchCount({ attr_num, item_cd, cate_cd }),
]);

useSetSekouQuery(data);

if (data.value?.query?.item.item_name) {
  useBreadcrumbs(() => [
    { name: "TOP", path: "/" },
    { name: "施工例", path: "/sekou" },
    {
      name: `商品「${
        (data.value?.query?.item.item_name ?? "") +
        (data.value?.query?.item.src_name ?? "")
      }」の施工例`,
      path: `/sekou/item?item_cd=${data.value?.query?.item.item_cd}`,
    },
  ]);
}

const pageTxt = current.value > 0 ? `${current.value + 1}ページ` : "";

if (data.value && !data.value.meta?.length) {
  const count = countData.value.count;

  const title = `外構・庭・リフォーム工事の施工例(${count}件)`;

  const description = `外構、お庭、リフォーム工事の施工例一覧ページです。${pageTxt}おしゃれな新築オープン外構工事や目隠しフェンス、玄関アプローチなどの施工例をご覧いただけます。ファミリー庭園ではお客様のご要望に合わせた外構プランをご提案しています。本当に必要なものだけを質の高い工事・低価格でご提供します。ファミリー庭園【正規】`;

  useHead({
    title,
    meta: [
      {
        name: "description",
        content: description,
      },
    ],
  });
} else if (data.value && data.value.meta?.length) {
  const count = countData.value.count;
  const meta = data.value.meta[0];

  let description = "";
  if (meta.description) {
    description = meta.description?.replace(/%cnt%/, count.toString());
    description = `${meta.h1txt?.replace(
      "　",
      "",
    )}一覧です。${pageTxt} ${description}`;
  } else {
    description = `施工例一覧ページです。${
      current.value > 0 ? `${current.value + 1}p` : ""
    } ファミリー庭園(関西7店舗)外構エクステリア工事の施工例公開件数${count}件公開中件で日本最大数。見積無料・10年保証・契約者には木製ブランコプレゼント。`;
  }

  useHead({
    title:
      meta.title?.replace(/%cnt%/, count.toString()) ||
      `外構・庭・リフォーム工事の施工例(${count}件)`,
    meta: [
      {
        name: "description",
        content: description,
      },
    ],
  });
}
</script>

<template>
  <div v-if="data" class="pc:px-8 relative min-h-full mb-16">
    <SekouHeader
      :params="{
        count: countData.count,
        current: current,
        max: countData.max,
        perPage: perpage,
      }"
    />
    <div class="min-h-screen">
      <transition name="fade">
        <ListResponsiveWrapper
          v-if="!pending"
          :tablet="3"
          class="mb-16"
          :class="{ visibility: pending ? 'hidden' : 'visible' }"
        >
          <SekouDetailLink
            v-for="(s, idx) in data.list"
            :key="s.sekou_cd"
            :index="idx"
            :sekou_cd="s.sekou_cd"
            :num="s.imgnum"
            :img_path="s.img_path"
            :title_1="s.title_1"
            :title_2="s.title_2"
            :area_1="s.area_1"
            :area_2="s.area_2"
          >
            <template #default>
              <span class="px-2 line-clamp-2">{{ s.area_1 + s.area_2 }}</span>
            </template>

            <template #outer>
              <SekouAttrTags
                class="p-2"
                @click.prevent
                :attrs="attrs"
                :attr_nums="s.attr_nums"
                :size="12"
                :space-between="12"
              />
            </template>
          </SekouDetailLink>
        </ListResponsiveWrapper>
      </transition>
      <div
        v-if="!pending && !data.list.length"
        class="w-full text-center py-32"
      >
        <p class="mb-4">この条件に一致する項目は見つかりませんでした</p>

        <SekouMenuFilterOpen>条件を再設定する</SekouMenuFilterOpen>
      </div>
    </div>

    <Pagination class="mb-8" :current="current" :max="data.max" />

    <NuxtLink to="https://famitei.mobi/contents.php/set_discounts">
      <ImageEl
        class="w-full"
        src="/images/footer/set.png"
        alt="同時購入セット割対象商品を見る"
        width="1000"
        height="154"
        sizes="300px md:600px lg:1000px"
        loading="lazy"
        format="webp"
      />
    </NuxtLink>

    <Loading class="absolute left-1/2 -translate-x-5 top-80" v-if="pending" />
  </div>
</template>
